import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// our components
import NorowsData from './NorowsData'
import Notification from './Notification'
import Carrier from './Carrier'
import OrderStatus from './OrderStatus'
import Customer from './Customer'
import CarrierTracking from './CarrierTracking'
import ShipmentRate from './ShipmentRate'
import Date from './Date'
import WidthMaximize from './WidthMaximize'
import TrackingDrawer from '../../pages/General/TrackingDrawer';
import OrderDrawer from '../General/OrderDrawer';

// mui components
import {
    FormControl,
    Pagination,
    Card,
    IconButton,
    Drawer,
    CardHeader,
    Paper,
    LinearProgress,
    Select,
    MenuItem,
    Typography,
    Stack
} from "@mui/material";
import { styled } from '@mui/material/styles';
import {
    DataGrid,
    GridToolbarContainer
} from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';

// Redux actions
import { fetchOrderDetail } from '../../pages/OrderDetail/orderdetailsSlice';
import { fetchCustomerDetails } from '../Reports/Deliveries/trackingEventsSlice';
import {
    fetchUnclosedShipments,
    resetDeliveriessNotifications
} from '../Reports/Deliveries/deliveriesmysqlSlice';

// Shared dictionary defining standard control focus layout rings
const enterpriseDrawerControlStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': { borderColor: '#CBD5E1' },
        '&.Mui-focused': {
            borderColor: '#1976d2',
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& input': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    },
    '& .MuiSelect-select': {
        padding: '8px 14px !important',
        fontSize: '13px',
        color: '#1E293B',
        fontWeight: 500
    }
};

const UnclosedShipmentDrawer = ({ visible, unclosedShipmentsOnClose }) => {
    const dispatch = useDispatch();
    const deliveries = useSelector(state => state.deliveriesmysql);
    const profile = useSelector(state => state.auth);
    
    // UPDATED: Pointing to unclosedShipmentData meta
    const [meta, setMeta] = useState(deliveries.unclosedShipmentData.meta);
    const [trackingDrawerVisible, setTrackingDrawerVisible] = useState(false);
    const [orderDrawerVisible, setOrderDrawerVisible] = useState(false);
    const [isMultiPiece, setIsMultiPiece] = useState(false);
    const [drawerWidth, setDrawerWidth] = useState("80%");
    
    const ondrawerclose = () => {
        dispatch(resetDeliveriessNotifications());
        unclosedShipmentsOnClose(false);
    }

    let columns = [
        {
            field: 'order_id',
            headerName: 'Order',
            headerClassName: 'super-app-theme--header',
            minWidth: 180,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                return <span style={{ color: 'rgb(24, 144, 255)', cursor: 'pointer' }} onClick={e => { orderCallback(params.row.order_id) }}>{params.row.order_id}</span>
            }
        },
        {
            field: 'master_tracking_number',
            headerName: 'Tracking Number',
            minWidth: 180,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                let tracking_number = params.row.master_tracking_number || "";
                let carrier_name = params.row.label?.carrier_name || "";
                let message = (params.row.manifest && params.row.manifest.is_closed === false)
                    ? "This shipment manifest is not yet closed" : null;
                let tracking = { "tracking_number": tracking_number, "carrier_name": carrier_name };
                return (
                    <CarrierTracking trackingItems={tracking} dispatchCallback={trackingCallback} carrierImageWidth="60px" message={message} />
                )
            }
        },
        {
            field: 'status',
            headerName: 'Shipment Status',
            minWidth: 180,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                return <OrderStatus orderStatus={params.row.status} />
            }
        },
        {
            field: 'customer_first_name',
            headerName: 'Customer',
            minWidth: 170,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                let receiver = params.row.address?.receiver;
                let name = "";
                if (receiver) {
                    name = receiver.first_name || "";
                    if (receiver.last_name) {
                        name = name + " " + receiver.last_name;
                    }
                }
                return <Customer name={name} />
            }
        },
        {
            field: 'shipment_date',
            headerName: 'Shipment Date',
            minWidth: 180,
            flex: 1,
            renderCell: (params) => {
                if (params.row && params.row.created_at) {
                    return <Date date={params.row.created_at} />
                } else {
                    return "-"
                }
            }
        },
        {
            field: 'rate_total_charges',
            headerName: 'Rate',
            sortable: false,
            flex: 1,
            minWidth: 100,
            renderCell: (params) => {
                let voided = params.row.label?.is_voided ? true : false;
                return <ShipmentRate
                    billed_rate={params.row.rate?.breakdown?.total_charge}
                    api_rate={params.row.rate?.customer_price}
                    is_multi_piece={params.row.is_multi_piece}
                    is_voided={voided}
                />
            }
        },
    ];

    // UPDATED: Sync internal meta with Redux meta if needed
    useEffect(() => {
        if (visible) {
            setMeta(deliveries.unclosedShipmentData.meta);
        }
    }, [visible]);

    // UPDATED: Fetching using the specific unclosed shipment action
    useEffect(() => {
        if (visible) {
            dispatch(fetchUnclosedShipments({
                ...meta,
                unclosed_shipment: true
            }));
        }
    }, [dispatch, meta, visible]);

    const orderCallback = (item) => {
        dispatch(fetchOrderDetail(item));
        setOrderDrawerVisible(true);
    }

    const orderOnClose = () => {
        setOrderDrawerVisible(false);
    }

    const trackingCallback = (carriername, trackingNumber) => {
        let apikey = (profile && profile.profile && profile.profile.api_token) ? profile.profile.api_token : "";
        const data = { 'tracking_number': trackingNumber };
        if(trackingNumber.charAt(0) === 'M'){
          setIsMultiPiece(true);
        }
        dispatch(fetchCustomerDetails(data, apikey));
        setTrackingDrawerVisible(true);
    }

    const onSortChange = (currentSorting) => {
        setMeta({ ...meta, sorting: JSON.stringify(currentSorting) });
    }

    const onChangePerPage = (event) => {
        setMeta({ ...meta, page: 1, per_page: event.target.value });
    }

    const onChangePagination = (event, page) => {
        setMeta({ ...meta, page: page });
    }

    function CustomTotalRows() {
        // UPDATED: Use unclosedShipmentData for row counts
        const dataLength = deliveries?.unclosedShipmentData?.data?.length || 0;
        const totalRows = deliveries?.unclosedShipmentData?.meta?.total || 0;
        return (
            <Box 
                className="custom-total-rows"
                sx={{ 
                    display: 'inline-flex', 
                    alignItems: 'center',
                    py: 0.6,
                    px: 1.5,
                    borderRadius: '8px',
                    backgroundColor: '#F1F5F9', 
                    border: '1px solid #E2E8F0'
                }}
            >
                <Typography variant="body2" sx={{ fontSize: '12.5px', fontWeight: 600, color: '#475569' }}>
                    Showing <strong style={{ color: '#0F172A', fontWeight: 700 }}>{dataLength}</strong> of <strong style={{ color: '#0F172A', fontWeight: 700 }}>{totalRows}</strong>
                </Typography>
            </Box>
        )
    }

    function CustomPagesizeoptions() {
        return (
            <Box className="custom-page-size-options" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>Show</Typography>
                    <FormControl size="small" variant="outlined" sx={{ minWidth: 80, ...enterpriseDrawerControlStyles }}>
                        <Select value={meta?.per_page} onChange={onChangePerPage}>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>entries</Typography>
                </Stack>
            </Box>
        )
    }

    function CustomToolbar() {
        return (
            <GridToolbarContainer
                sx={{
                    p: 2,
                    bgcolor: '#F8FAFC',
                    borderBottom: '1px solid #E2E8F0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    width: '100%',
                    boxSizing: 'border-box'
                }}
            >
                <CustomPagesizeoptions />
                <CustomTotalRows />
            </GridToolbarContainer>
        );
    }

    function CustomPagination() {
        // UPDATED: Use unclosedShipmentData meta for pagination
        return (
            <Box
                sx={{
                    p: 1.5,
                    bgcolor: '#F8FAFC',
                    borderTop: '1px solid #E2E8F0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    width: '100%',
                    boxSizing: 'border-box',
                    '& .MuiPaginationItem-root': {
                        fontSize: '13px',
                        fontWeight: 600,
                        borderRadius: '8px',
                        '&.Mui-selected': {
                            backgroundColor: '#1976d2',
                            color: '#FFFFFF',
                            '&:hover': { backgroundColor: '#115293' }
                        }
                    }
                }}
            >
                <Pagination
                    color="primary"
                    shape="circular"
                    page={deliveries?.unclosedShipmentData?.meta?.current_page || 1}
                    count={deliveries?.unclosedShipmentData?.meta?.last_page || 1}
                    onChange={onChangePagination}
                />
            </Box>
        );
    }

    return (
        <Drawer
            anchor="right"
            open={visible}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") ondrawerclose();
            }}
            PaperProps={{ 
                sx: { 
                    width: drawerWidth,
                    backgroundColor: '#F8FAFC',
                    borderLeft: '1px solid #E2E8F0'
                } 
            }}
        >
            {deliveries?.unclosedShipmentData?.notification?.show &&
                <Notification
                    is_open={deliveries.unclosedShipmentData.notification.show}
                    messageType={deliveries.unclosedShipmentData.notification.messageType}
                    messages={deliveries.unclosedShipmentData.notification.messageContent}
                    handleClose={() => dispatch(resetDeliveriessNotifications())}
                />}
            <Box sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
                <Card 
                    className="sc--cardhead--details"
                    sx={{ 
                        width: '100%',
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: '16px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
                        overflow: 'hidden'
                    }}
                >
                    <Box sx={{ width: '100%', background: '#fff' }}>
                        <CardHeader 
                            title={
                                <Typography variant="h6" component="h2" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px' }}>
                                    Unclosed Shipment Details
                                </Typography>
                            } 
                            action={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <WidthMaximize defaultWidth="80%" onToggle={setDrawerWidth} />
                                    <IconButton 
                                        onClick={ondrawerclose}
                                        sx={{ 
                                            color: '#64748B',
                                            '&:hover': { backgroundColor: '#F1F5F9', color: '#1E293B' }
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: '20px' }} />
                                    </IconButton>
                                </Stack>
                            } 
                            sx={{ p: 2.5, borderBottom: '1px solid #E2E8F0', backgroundColor: '#FFFFFF' }}
                        />
                        <DataGrid
                            sx={{
                                border: 'none',
                                backgroundColor: '#FFFFFF',
                                overflow: 'hidden',
                                '& .MuiDataGrid-topContainer': { backgroundColor: '#F8FAFC', '&::after': { display: 'none' } },
                                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', minHeight: '52px !important', maxHeight: '52px !important' },
                                '& .MuiDataGrid-columnHeader': { color: '#475569', fontWeight: 700, fontSize: '13px', '&:focus, &:focus-within': { outline: 'none' } },
                                '& .MuiDataGrid-row': { transition: 'background-color 0.15s ease-in-out', borderBottom: '1px solid #F1F5F9', alignItems: 'center', '&:hover': { backgroundColor: '#F8FAFC' }, '&.Mui-selected': { backgroundColor: 'rgba(25, 118, 210, 0.04)', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' } } },
                                '& .MuiDataGrid-cell': { fontSize: '13.5px', color: '#334155', borderBottom: 'none', display: 'flex', alignItems: 'center', py: '12px', '&:focus, &:focus-within': { outline: 'none' } },
                                '& .MuiLinearProgress-root': { height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } }
                            }}
                            getRowId={(row) => row._id}
                            // UPDATED: Data source from unclosedShipmentData
                            rows={deliveries?.unclosedShipmentData?.data || []}
                            columns={columns}
                            autoHeight
                            disableColumnFilter
                            disableColumnMenu
                            sortingMode="server"
                            onSortModelChange={onSortChange}
                            sortModel={meta?.sorting ? JSON.parse(meta.sorting) : []}
                            disableRowSelectionOnClick
                            slots={{
                                loadingOverlay: LinearProgress,
                                footer: CustomPagination,
                                toolbar: CustomToolbar,
                                noRowsOverlay: NorowsData
                            }}
                            columnVisibilityModel={{
                                rate_total_charges: profile?.profile?.hide_rate !== true, 
                            }}
                            // UPDATED: Loading state from unclosedShipmentData
                            loading={deliveries?.unclosedShipmentData?.loading}
                        />
                    </Box>
                </Card>
            </Box>
            <TrackingDrawer visible={trackingDrawerVisible} isMultiPiece={isMultiPiece} onClose={() => setTrackingDrawerVisible(false)} />
            <OrderDrawer visible={orderDrawerVisible} onClose={orderOnClose} />
        </Drawer>
    )
}

export default UnclosedShipmentDrawer;